/* eslint-disable import/prefer-default-export */
/**
 * @description pure functions for formatting values for display purposes
 */

import { DateTime } from 'luxon';

/**
 * Formats ISO date strings into a long human readable format (e.g. Thursday, 01/03/2023, 5:40 PM)
 * @param {string} value - date value to be formatted
 * @returns {string} - formatted date
 */
export function formatLongIsoDate (value) {
  return DateTime.fromISO(value).toLocaleString({
    weekday: 'long',
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
}

/**
 * Formats a list of strings into a single list phrase string
 * @param {string[]} list - list of strings to format
 * @returns {string} - list expressed as a single list phrase string
 */
export function formatListAsString (list) {
  // If the list is null, undefined, or empty, return nothing
  if (!list || list.length === 0) {
    return '';
  // If the list is a single entry long, just return the single entry
  } else if (list.length === 1) {
    return list[0];
  // Lists that are two entries long can be joined with a single "and"
  } else if (list.length === 2) {
    return `${ list[0] } and ${ list[1] }`;
  }

  // Retrieve the list entry in the list (because it will be joined by an "and") and then join the rest with a comma and space
  const newList = list.slice(0);
  const lastEntry = newList.pop();

  return `${ newList.join(', ') }, and ${ lastEntry }`;
}

const COUNTRY_CODE_REGEX = /^\+\d+/;
const US_COUNTRY_CODE_REGEX = /^\+1/;
const US_NUMBER_REGEX = /^\d{10}$/;
const LETTERS_REGEX = /[a-z]+/i;

/**
 * Formats US phone number strings into a normalized format, XXX-XX-XXXX
 * If unable to format the string, then the original value is returned
 * @param {string} str - string to be parsed
 * @returns {string} - formatted string
 */
export function formatPhone (str) {
  const trimmedStr = str?.trim();
  let testStr = trimmedStr;

  try {
    // If the phone number is empty after trimming it, return the trimmed string
    if (trimmedStr.length === 0) {
      return trimmedStr;
    }

    const countryCode = trimmedStr.match(COUNTRY_CODE_REGEX)?.[0];

    // If the phone number has a country code
    if (countryCode) {
      // If its a US country code, remove it and continue parsing
      if (countryCode === '+1') {
        testStr = testStr.replace(COUNTRY_CODE_REGEX, '');
      // If the country code is a US phone number written as +1XXXXXXXX, remove the +1
      } else if (countryCode.length === 12 && US_COUNTRY_CODE_REGEX.test(countryCode)) {
        testStr = testStr.replace(US_COUNTRY_CODE_REGEX, '');
      // If the country code is a US phone number written as +XXXXXXXXX, remove the +
      } else if (countryCode.length === 11) {
        testStr = testStr.replace('+', '');
      // Otherwise, its an international country code, return the trimmed string
      } else {
        return trimmedStr;
      }
    }

    // If the phone number contains a letter for things like extensions, return the trimmed string
    if (LETTERS_REGEX.test(testStr)) {
      return trimmedStr;
    }

    // Strip out any special characters from the test string
    testStr = testStr.split('').filter((char) => /[\d]/.test(char)).join('');

    // If the test string matches a US phone number, return the string formatted as XXX-XX-XXXX
    if (US_NUMBER_REGEX.test(testStr)) {
      return `${ testStr.substr(0, 3) }-${ testStr.substr(3, 3) }-${ testStr.substr(6) }`;
    }
  } catch (_) {
    // If an error was encounted, return the string
    return trimmedStr;
  }

  // If all else fails, return the trimmed string
  return trimmedStr;
}
